import { createContext, useContext, useRef } from "react"
import { create, useStore } from "zustand"
import { chLegalGuardianAttestationFragment$key } from "@/modules/patients/detail/legal-guardian-attestation/ch/services/__generated__/chLegalGuardianAttestationFragment.graphql"
import { deLegalGuardianAttestationFragment$key } from "@/modules/patients/detail/legal-guardian-attestation/de/services/__generated__/deLegalGuardianAttestationFragment.graphql"

type State = {
  progressNoteId: string | null
  legalGuardianAttestationId: string | null
  legalGuardianAttestationRef: chLegalGuardianAttestationFragment$key | deLegalGuardianAttestationFragment$key | null
}

type Actions = {
  setProgressNoteId: (progressNoteId: string | null) => void
  setLegalGuardianAttestation: (
    legalGuardianAttestation: string | null,
    ref: chLegalGuardianAttestationFragment$key | deLegalGuardianAttestationFragment$key | null,
  ) => void
  reset: () => void
}

type LegalGuardianAttestationDrawerStore = State & Actions
type LegalGuardianAttestationDrawerStoreType = ReturnType<typeof createLegalGuardianAttestationDrawerStore>

const initialState: State = {
  progressNoteId: null,
  legalGuardianAttestationId: null,
  legalGuardianAttestationRef: null,
}

const createLegalGuardianAttestationDrawerStore = () => {
  return create<LegalGuardianAttestationDrawerStore>((set, get) => ({
    ...initialState,
    setProgressNoteId: (progressNoteId) => {
      get().reset()
      set({ progressNoteId })
    },
    setLegalGuardianAttestation: (legalGuardianAttestationId, ref) =>
      set({ legalGuardianAttestationId, legalGuardianAttestationRef: ref }),
    reset: () => {
      set(initialState)
    },
  }))
}

const LegalGuardianAttestationDrawerContext = createContext<LegalGuardianAttestationDrawerStoreType | null>(null)

type LegalGuardianAttestationDrawerProviderProps = React.PropsWithChildren

export const LegalGuardianAttestationDrawerProvider = ({ children }: LegalGuardianAttestationDrawerProviderProps) => {
  const storeRef = useRef<LegalGuardianAttestationDrawerStoreType>(undefined)
  if (!storeRef.current) {
    storeRef.current = createLegalGuardianAttestationDrawerStore()
  }
  return (
    <LegalGuardianAttestationDrawerContext.Provider value={storeRef.current}>
      {children}
    </LegalGuardianAttestationDrawerContext.Provider>
  )
}

export function useLegalGuardianAttestationDrawerContext<T>(
  selector: (state: LegalGuardianAttestationDrawerStore) => T,
): T {
  const store = useContext(LegalGuardianAttestationDrawerContext)
  if (!store) throw new Error("Missing LegalGuardianAttestationDrawerProvider in the tree")
  return useStore(store, selector)
}
